<!-- eslint-disable vue/no-v-html -- Trusted input from surce code -->
<template>
  <component
    :is="iconData.Component"
    v-if="iconData.tag === 'lucide'"
    :color="color"
    :class="classes"
    :fill="computedFill"
    v-bind="rootProps"
  />
  <svg
    v-else
    :class="classes"
    :viewBox="iconData.iconDef.viewBox"
    v-bind="rootProps"
    v-html="iconData.iconDef.path"
  ></svg>
</template>
<!-- eslint-enable -->

<script lang="ts" setup>
import { computed, type SVGAttributes } from 'vue'
import * as lucideIcons from 'lucide-vue-next'
import { reactiveOmit } from '@vueuse/core'

import type { IconColor, IconSize } from './types'
import {
  type IconName,
  isLucideIconName,
  kebabToPascal,
  type LucideIconComponentName,
} from './IconName'
import type { IconDeff } from '../../lib/all-icons'
import iconPaths from '../../lib/internal-icons'

type IconProps = {
  /**
   * Defines the icon to be displayed.
   */
  name: IconName

  /**
   * Defines the color of the icon.
   */
  color?: IconColor

  /**
   * Defines the size of the icon. It should be 'normal' or 'large'.
   */
  size?: IconSize

  /**
   * Defines whether the icon should be filled or not.
   * Defaults to false if not provided.
   */
  isFilled?: boolean
  /**
   * Defines the class to be applied to the icon.
   */
  customClass?: string
} & /* @vue-ignore */ Omit<SVGAttributes, 'name' | 'color' | 'fill' | 'viewBox'>

const props = withDefaults(defineProps<IconProps>(), {
  color: undefined,
  size: undefined,
  isFilled: false,
})

const rootProps = reactiveOmit(props, [
  'name',
  'color',
  'size',
  'isFilled',
  'customClass',
])

const iconData = computed<
  | {
      tag: 'lucide'
      Component: (typeof lucideIcons)[LucideIconComponentName]
    }
  | {
      tag: 'internal'
      iconDef: IconDeff
    }
>(() => {
  const name = props.name

  if (isLucideIconName(name)) {
    return {
      tag: 'lucide',
      Component: lucideIcons[kebabToPascal(name)],
    }
  } else {
    const keyToReplace = 'ID_TO_REPLACE'
    const randomId = Math.random().toString(36).substring(2, 15)

    const iconDef = iconPaths[name]
    return {
      tag: 'internal',
      iconDef: {
        ...iconDef,
        path: iconDef.path.replaceAll(keyToReplace, randomId),
      },
    }
  }
})

const classes = computed(() => {
  return {
    'sb-icon': true,
    'sb-icon--custom': !isLucideIcon.value,
    'sb-icon--lucide': isLucideIcon.value,
    'sb-icon--fill': props.isFilled,
    'sb-icon--no-fill':
      iconData.value.tag === 'internal' && iconData.value.iconDef.fill,
    [`sb-icon--${props.size}`]: props.size,
    [`sb-icon--color-${props.color}`]: props.color,
    'sb-icon--loading': props.name === 'loading',
    'sb-icon--refreshing': props.name === 'refreshing',
  }
})

const computedFill = computed(() => (props.isFilled ? 'currentColor' : 'none'))

const isLucideIcon = computed(() => iconData.value.tag === 'lucide')
</script>
